import Chip from '@mui/material/Chip';
import { useEffect, useMemo, useState } from 'react';
import { accessApi } from '../access.api';
import { adminEmployeesApi, departmentsApi, usersApi } from '../admin.api';
import ResourceManager, {
  FieldOption,
  ResourceColumn,
  ResourceField,
} from '../components/ResourceManager';

/**
 * `users.department` is a plain text column, not a foreign key to `departments`, so the
 * suggestions carry the department *name* as their value. Existing rows can hold anything
 * typed before this list existed, which is why the field stays free text.
 */
const loadDepartmentOptions = async (): Promise<FieldOption[]> => {
  const departments = await departmentsApi.list();

  return departments
    .filter((department) => Boolean(department.name))
    .map((department) => ({ label: department.name, value: department.name }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Employee records to pick from for "Employee Number". The value saved is the employee
 * code (SDLLP-xx): a login whose number is an employee's code acts as that employee, so
 * a second login for the same person gets their tasks under a different role. The label
 * carries the name and department so the admin can search by either.
 */
const loadEmployeeOptions = async (): Promise<FieldOption[]> => {
  const employees = await adminEmployeesApi.list();

  return employees
    .filter((employee) => Boolean(employee.employeeId))
    .map((employee) => {
      const user = employee.user as { name?: string } | undefined;
      const department = employee.department as { name?: string } | undefined;
      const parts = [user?.name, department?.name ? `(${department.name})` : null].filter(Boolean);
      return {
        label: parts.length ? `${employee.employeeId} — ${parts.join(' ')}` : String(employee.employeeId),
        value: String(employee.employeeId),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

const columns: ResourceColumn[] = [
  { id: 'id', dataKey: 'id', label: 'ID', align: 'center', sortable: true },
  { id: 'name', dataKey: 'name', label: 'Name', align: 'left', sortable: true },
  { id: 'username', dataKey: 'username', label: 'Username', align: 'left', sortable: true },
  { id: 'employeenumber', dataKey: 'employeenumber', label: 'Emp. No.', align: 'left' },
  { id: 'department', dataKey: 'department', label: 'Department', align: 'left' },
  { id: 'mobile', dataKey: 'mobile', label: 'Mobile', align: 'left' },
  { id: 'email', dataKey: 'email', label: 'Email', align: 'left' },
  {
    id: 'role',
    dataKey: 'role',
    label: 'Role',
    align: 'center',
    sortable: true,
    renderCell: (value: string | null) =>
      value ? (
        <Chip label={value.toUpperCase()} size='small' color={value === 'admin' ? 'primary' : 'default'} />
      ) : (
        <Chip label='NO ROLE' size='small' variant='outlined' />
      ),
  },
];

/**
 * `password` is deliberately last and never pre-filled: `ResourceManager` drops
 * empty keys from the payload, so leaving it blank on edit keeps the existing credential
 * and typing in it is what triggers a reset. A new user must have one.
 */
const fieldsFor = (roleOptions: FieldOption[]): ResourceField[] => [
  { name: 'name', label: 'Full Name', required: true },
  { name: 'username', label: 'Username', required: true },
  {
    name: 'employeenumber',
    label: 'Employee Number',
    type: 'autocomplete',
    loadOptions: loadEmployeeOptions,
    helperText:
      "Type a code or name to search employees. Picking one links this login to that employee's tasks and schedule.",
  },
  {
    name: 'department',
    label: 'Department',
    type: 'autocomplete',
    loadOptions: loadDepartmentOptions,
    helperText: 'Type to search the departments list, or enter a value that is not on it.',
  },
  { name: 'mobile', label: 'Mobile' },
  { name: 'email', label: 'Email' },
  {
    name: 'role',
    label: 'Role',
    type: 'select',
    options: roleOptions,
    helperText: 'The menus this user sees come from the role (Administration → Roles & Menu Access).',
  },
  {
    name: 'password',
    label: 'Password',
    type: 'password',
    requiredOnCreate: true,
    helperText: 'At least 6 characters. When editing, leave blank to keep the current password.',
  },
];

/**
 * Admin-only user list. "Add User" opens the same popup as edit; users are not deleted
 * here, since tasks, attendance and history hang off them.
 */
export default function UsersPage() {
  // Roles are defined on Roles & Menu Access, so the choices come from there.
  const [roleOptions, setRoleOptions] = useState<FieldOption[]>([]);
  useEffect(() => {
    accessApi
      .roles()
      .then((roles) => setRoleOptions(roles.map((r) => ({ label: r.label, value: r.code }))))
      .catch(() => setRoleOptions([]));
  }, []);
  const fields = useMemo(() => fieldsFor(roleOptions), [roleOptions]);

  return (
    <ResourceManager
      title='USERS'
      entityName='User'
      client={usersApi}
      columns={columns}
      fields={fields}
      canDelete={false}
    />
  );
}
